"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const SERVICE_AREAS = ["Point Fredrick", "Point clare", "Koolawong", "Woy woy", "Umina", "Avoca", "Bateau bay", "Chittaway Bay", "Berkley vale", "Lake Haven", "Budgewoi", "Charmhaven", "Central Mangrove", "Holgate", "East Gosford", "Ettalong Beach", "Long Jetty", "Jilliby", "Kangy Angy", "Hillarney Vale", "Killcare", "Kincumber", "Kulnura", "Somersby", "Mangrove Mountain", "Norah Head", "Mardi", "Noraville", "Palmdale", "Phegans Bay", "Saratoga", "Shelly Beach", "Springfield", "Tacoma", "Tascot", "The Entrance", "Watanobbi", "Wamberal", "Tumbi Umbi", "Toukley", "Alison", "Fountaindale"];
export default function ServicingNararaSurroundingAreas() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cloudRef,
    isVisible: isCloudVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section id="servicing-narara-surrounding-areas" className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-b from-chart-2/10 via-chart-3/5 to-transparent rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-48 -left-24 w-[900px] h-[900px] bg-gradient-to-tr from-chart-3/10 to-transparent rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-3 bg-secondary rounded-full mb-6 shadow-sm">
            <MapPin className="w-6 h-6 text-chart-2" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            Servicing Narara &{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-chart-1 to-chart-2">
              Surrounding Areas
            </span>
          </h2>
          <p className="text-lg text-muted-foreground">
            We provide reliable, premium plumbing solutions across the Central Coast. 
            Our local expertise ensures fast response times when you need us most.
          </p>
        </div>

        <div ref={cloudRef} className={`mt-16 flex flex-wrap justify-center gap-3 md:gap-4 transition-all duration-1000 delay-200 ease-out ${isCloudVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {SERVICE_AREAS.map((suburb, i) => <div key={suburb} data-index={i} className="group relative px-5 py-2.5 md:px-6 md:py-3 rounded-full bg-background border border-chart-2/20 shadow-sm hover:shadow-md transition-all duration-300 cursor-default overflow-hidden">
              {}
              <div className="absolute inset-0 bg-gradient-to-r from-chart-2 to-chart-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {}
              <span className="relative z-10 text-sm md:text-base font-medium text-foreground/80 group-hover:text-primary-foreground transition-colors duration-300 flex items-center gap-2">
                {suburb}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}